<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import CaptainResponseAPI from 'dashboard/api/captain/response';
import Banner from 'dashboard/components/ui/Banner.vue';

const REFRESH_INTERVAL = 15 * 60 * 1000;
const PENDING_PAGE_ROUTE_NAME = 'captain_assistants_responses_pending';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();

const currentUser = useMapGetter('getCurrentUser');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isCaptainEnabled = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.CAPTAIN)
);

const isOnPendingPage = computed(() => route.name === PENDING_PAGE_ROUTE_NAME);

const summary = ref([]);
const dismissed = ref(false);
let refreshTimer = null;

const dismissKey = computed(() => {
  const localDate = new Date().toLocaleDateString();
  return `chatwoot_captain_suggestions_banner_dismissed_${accountId.value}_${currentUser.value?.id}_${localDate}`;
});

const isDismissedForToday = () => {
  try {
    return window.localStorage.getItem(dismissKey.value) === 'true';
  } catch {
    return false;
  }
};

const persistDismissal = () => {
  try {
    window.localStorage.setItem(dismissKey.value, 'true');
  } catch {
    // If storage is unavailable, the banner simply reappears next time.
  }
};

const topAssistant = computed(() => summary.value[0] || null);

const totalPendingCount = computed(() =>
  summary.value.reduce((total, item) => total + item.pending_count, 0)
);

const bannerMessage = computed(() => {
  if (!topAssistant.value) return '';

  if (summary.value.length === 1) {
    return t('CAPTAIN_SUGGESTIONS_BANNER.SINGLE_ASSISTANT', {
      assistantName: topAssistant.value.assistant_name,
      count: topAssistant.value.pending_count,
    });
  }

  return t('CAPTAIN_SUGGESTIONS_BANNER.MULTIPLE_ASSISTANTS', {
    count: totalPendingCount.value,
  });
});

const shouldShowBanner = computed(() => {
  if (!isCaptainEnabled.value || isOnPendingPage.value || dismissed.value) {
    return false;
  }
  return !!topAssistant.value;
});

const fetchSummary = async () => {
  if (!isCaptainEnabled.value) return;

  try {
    const response = await CaptainResponseAPI.getPendingSummary();
    summary.value = response.data.payload;
  } catch {
    // Swallow errors silently, no banner is shown when the summary fails to load.
    summary.value = [];
  }
};

const handlePrimaryAction = () => {
  if (!topAssistant.value) return;

  router.push({
    name: PENDING_PAGE_ROUTE_NAME,
    params: {
      accountId: accountId.value,
      assistantId: topAssistant.value.assistant_id,
    },
  });
};

const handleClose = () => {
  persistDismissal();
  dismissed.value = true;
};

// Refresh as soon as the flags load, the account changes, or the user leaves the
// review page (they may have just cleared the queue), not only on the timer.
watch(isCaptainEnabled, enabled => enabled && fetchSummary());
watch(accountId, () => {
  dismissed.value = isDismissedForToday();
  fetchSummary();
});
watch(isOnPendingPage, (onPage, wasOnPage) => {
  if (wasOnPage && !onPage) fetchSummary();
});

onMounted(() => {
  dismissed.value = isDismissedForToday();
  fetchSummary();
  refreshTimer = setInterval(fetchSummary, REFRESH_INTERVAL);
});

onUnmounted(() => {
  clearInterval(refreshTimer);
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Banner
    v-if="shouldShowBanner"
    color-scheme="primary"
    :banner-message="bannerMessage"
    action-button-icon="i-lucide-arrow-right"
    :action-button-label="t('CAPTAIN_SUGGESTIONS_BANNER.ACTION_BUTTON')"
    has-action-button
    has-close-button
    @primary-action="handlePrimaryAction"
    @close="handleClose"
  />
</template>
